import React, { useEffect, useState } from 'react'
import { View, StyleSheet, TouchableOpacity } from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import { Button, DeleteModal, Icon, ShareProfileModal, Text } from '@components'
import { Theme } from '@theme'
import { getCommonStyles, showApiErrorToast, showSuccessToast } from '@utils'
import { useContactMappingStore, useContactsListStore, useCurrentUserStore } from '@store'
import { GhostFilterButtons } from './GhostFilterButtons'
import { GhostTabButton } from './GhostTabButton'
import { Profile, useInitiateIndividualConversation, useInviteGhostToJoin, useScreen } from '@hooks'
import { GhostInfoSection } from './GhostInfoSection'
import { SocialNetworkSection } from '../../../profile/components'
import { useLanguage } from '@context'
import { getGhostInviteDate, rememberGhostInvite } from '../../../../utils/ghostInvites'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)

interface GhostActionButtonsProps {
  isGhost?: boolean
  isExtension?: boolean
  contact: Profile
  isRequestPending?: boolean
  onAddPress?: () => void
  // This person's own request to me is waiting: the button answers it.
  hasReceivedRequest?: boolean
  onAnswerPress?: () => void
  // Given once the id of my waiting request is known: the button cancels it.
  onCancelRequestPress?: () => void
  selectedTab: 'about' | 'note' | 'reminder' | 'memory'
  setSelectedTab: (selectedTab: 'about' | 'note' | 'reminder' | 'memory') => void
  onHashtagsPress?: () => void
  onCategoriesPress?: () => void
}

export const GhostActionButtons: React.FC<GhostActionButtonsProps> = ({
  isGhost,
  isExtension,
  contact,
  isRequestPending = false,
  selectedTab,
  onAddPress,
  hasReceivedRequest = false,
  onAnswerPress,
  onCancelRequestPress,
  setSelectedTab,
  onHashtagsPress,
  onCategoriesPress,
}) => {
  const theme = useTheme()
  const styles = getStyle(theme)
  const commonStyles = getCommonStyles(theme)
  const navigation = useNavigation()
  const { isDesktop, isMobile, isWeb } = useScreen()
  const { t, currentLanguage } = useLanguage()

  const { userID } = useCurrentUserStore()
  const isMe = contact?.accountId === userID

  const { getScope, getCommonContacts } = useContactMappingStore()
  const { contacts: allContacts } = useContactsListStore()
  const relatedContacts = allContacts.filter((contact) =>
    getCommonContacts(contact.id)
      .map((c) => c.id)
      .includes(contact.id),
  )

  const { mutateAsync: initiateIndividualConversation } = useInitiateIndividualConversation()
  const { mutateAsync: inviteGhostToJoin, isPending: isInvitePending } = useInviteGhostToJoin()

  const [isShareModalVisible, setIsShareModalVisible] = useState(false)
  const [isInviteConfirmVisible, setIsInviteConfirmVisible] = useState(false)
  // The day this card was last invited from this device (the server keeps no trace).
  const [invitedAt, setInvitedAt] = useState<string | null>(null)
  const inviteEmail = contact?.contacts?.email?.trim()
  const contactName = `${contact?.firstName || ''} ${contact?.lastName || ''}`.trim()

  useEffect(() => {
    setInvitedAt(null)
    if (!isGhost || !userID || !contact?.id) return
    let isCurrent = true
    getGhostInviteDate(userID, contact.id).then((date) => {
      if (isCurrent) setInvitedAt(date)
    })
    return () => {
      isCurrent = false
    }
  }, [isGhost, userID, contact?.id])

  const handleEditProfile = () => {
    if (isGhost) {
      ;(navigation as any).navigate('Profile', {
        screen: 'EditGhostContact',
        params: {
          profileId: contact.id,
        },
      })
    }
  }

  const sendMessage = async () => {
    const conversation = await initiateIndividualConversation({
      participantAccountId: contact.accountId,
    })
    ;(navigation as any).navigate('Messages', {
      screen: 'Conversation',
      params: {
        conversationId: conversation.id,
      },
    })
  }

  const share = () => {
    setIsShareModalVisible(true)
  }

  const sendInviteToJoin = async () => {
    if (!contact?.id) return
    try {
      await inviteGhostToJoin({ ghostProfileId: contact.id, language: currentLanguage })
      if (userID) setInvitedAt(await rememberGhostInvite(userID, contact.id))
      showSuccessToast(t('ghostProfile.inviteSent'))
    } catch (error) {
      showApiErrorToast(t, error)
    }
  }

  // With an address on the card, say where the e-mail goes before sending it.
  // Without one the server refuses, and its answer explains why.
  const handleInviteToJoin = () => {
    if (inviteEmail) setIsInviteConfirmVisible(true)
    else sendInviteToJoin()
  }

  // « il y a 6 jours » opens the Notes tab, where « Historique avec X » tells
  // what the last interactions were.
  const openHistory = () => setSelectedTab('note')

  const lastInteractionLabel = contact?.lastInteractionAt
    ? dayjs(contact.lastInteractionAt).fromNow()
    : t('ghostProfile.noInteractionYet')
  // The last interaction is the last message exchanged: a card cannot receive
  // any, and one does not write to oneself, so « no interaction yet » would be
  // untrue there.
  const showLastInteraction = !isMe && (!isGhost || !!contact?.lastInteractionAt)

  const notFriend = !isMe && !contact?.scope
  return (
    <>
      {/* Bio spans the full content width above the desktop two-column row so a
          long bio is never squeezed into a left column beside the rail. */}
      {!isExtension && (
        <GhostInfoSection
          scope={getScope(contact?.id)}
          description={contact?.bio}
          relations={relatedContacts}
          hashtags={contact?.hashtags}
        />
      )}
      <View style={[commonStyles.flex, isDesktop && commonStyles.row]}>
        <View style={[commonStyles.flex, isExtension && commonStyles.paddingTop]}>
          <View
            style={[
              styles.refreshContainer,
              isWeb && isMobile && commonStyles.paddingBottom,
              // Alone on the phone row, the social icons stay on the right.
              !isDesktop && !isExtension && !showLastInteraction && styles.refreshContainerEnd,
            ]}
          >
            {!isDesktop && !isExtension && showLastInteraction && (
              <TouchableOpacity
                testID="last-interaction"
                style={styles.refreshContent}
                onPress={openHistory}
                disabled={!contact?.lastInteractionAt}
              >
                <Icon name="refresh" size={theme.iconSize.medium} />
                <Text type="bodySmall" color={theme.colors.greyBlue}>
                  {lastInteractionLabel}
                </Text>
              </TouchableOpacity>
            )}
            {(!isDesktop || isExtension) && (
              <SocialNetworkSection
                isSmall
                facebook={contact?.contacts?.facebook}
                instagram={contact?.contacts?.instagram}
                twitter={contact?.contacts?.twitter}
                linkedin={contact?.contacts?.linkedin}
                searchName={`${contact?.firstName || ''} ${contact?.lastName || ''}`.trim()}
              />
            )}
          </View>
          <View style={styles.container}>
            {!isExtension && (
              <View style={[commonStyles.flex, styles.marginBottom, isDesktop && commonStyles.row]}>
                {/* Bouton principal "Modifier le profil fantôme" */}
                <View style={[commonStyles.flex, commonStyles.row]}>
                  {isGhost && (
                    <Button
                      title={t(invitedAt ? 'ghostProfile.inviteAgain' : 'ghostProfile.inviteToJoin')}
                      onPress={handleInviteToJoin}
                      iconName="share"
                      iconColor={theme.colors.primary}
                      customHeight={48}
                      unfilled
                      isSmall
                      flex
                      disabled={isInvitePending}
                    />
                  )}
                  {notFriend && !isGhost && (
                    <Button
                      testID="profile-request-button"
                      title={
                        hasReceivedRequest
                          ? t('notifications.accept')
                          : isRequestPending
                            ? t('ghostProfile.requestSent')
                            : t('common.add')
                      }
                      onPress={() =>
                        hasReceivedRequest
                          ? onAnswerPress?.()
                          : isRequestPending
                            ? onCancelRequestPress?.()
                            : onAddPress?.()
                      }
                      iconName="plus"
                      iconColor={theme.colors.primary}
                      customHeight={48}
                      //borderWidth={1}
                      unfilled
                      isSmall
                      flex
                      disabled={!hasReceivedRequest && isRequestPending && !onCancelRequestPress}
                    />
                  )}
                  <Button
                    title={
                      isMe
                        ? t('ghostProfile.shareProfile')
                        : isGhost
                          ? t('ghostProfile.editProfile')
                          : t('ghostProfile.message')
                    }
                    onPress={isMe ? share : isGhost ? handleEditProfile : sendMessage}
                    iconName={isMe ? 'share' : isGhost ? 'editPen' : 'bubbleRound'}
                    iconColor={theme.colors.primary}
                    customHeight={48}
                    //borderWidth={1}
                    unfilled
                    isSmall
                    flex
                    disabled={notFriend && !isGhost}
                  />
                  {isMe && (
                    <Button
                      title={t('ghostProfile.edit')}
                      onPress={() => (navigation as any).navigate('ProfileEdit')}
                      iconName="editPen"
                      iconColor={theme.colors.primary}
                      customHeight={48}
                      //borderWidth={1}
                      unfilled
                      isSmall
                      flex
                    />
                  )}
                </View>
                {isDesktop && (
                  <SocialNetworkSection
                    isSmall
                    facebook={contact?.contacts?.facebook}
                    instagram={contact?.contacts?.instagram}
                    twitter={contact?.contacts?.twitter}
                    linkedin={contact?.contacts?.linkedin}
                    searchName={`${contact?.firstName || ''} ${contact?.lastName || ''}`.trim()}
                  />
                )}
              </View>
            )}
            {!isMe && !isDesktop && (
              <GhostFilterButtons
                profile={contact}
                onHashtagsPress={onHashtagsPress}
                onCategoriesPress={onCategoriesPress}
              />
            )}
          </View>
        </View>
        {isDesktop && (
          <View
            testID="ghost-detail-rail"
            style={[commonStyles.flex, commonStyles.alignEnd, styles.rightContainer]}
          >
            {showLastInteraction && (
              <TouchableOpacity
                testID="last-interaction"
                style={styles.refreshContentWeb}
                onPress={openHistory}
                disabled={!contact?.lastInteractionAt}
              >
                <Icon name="refresh" size={theme.iconSize.medium} />
                <Text type="bodySmall" color={theme.colors.greyBlue}>
                  {lastInteractionLabel}
                </Text>
              </TouchableOpacity>
            )}
            {!isMe && (
              <GhostFilterButtons
                profile={contact}
                onHashtagsPress={onHashtagsPress}
                onCategoriesPress={onCategoriesPress}
              />
            )}
          </View>
        )}
      </View>
      <View testID="ghost-detail-tabs" style={styles.actionButtonsContainer}>
        <GhostTabButton
          iconName="userCircle"
          label={t('ghostProfile.about')}
          isSelected={selectedTab === 'about'}
          onPress={() => setSelectedTab('about')}
        />
        {!isMe && (
          <GhostTabButton
            iconName="note"
            label={t('ghostProfile.notes')}
            disabled={isExtension}
            isSelected={selectedTab === 'note'}
            onPress={() => setSelectedTab('note')}
          />
        )}
        <GhostTabButton
          iconName="calendar"
          label={
            isMobile
              ? t('ghostProfile.appointments').split('/')?.[0] || t('ghostProfile.appointments')
              : t('ghostProfile.appointments')
          }
          disabled={isExtension}
          isSelected={selectedTab === 'reminder'}
          onPress={() => setSelectedTab('reminder')}
        />
        <GhostTabButton
          iconName="picture"
          label={t('ghostProfile.memories')}
          disabled={isExtension}
          isSelected={selectedTab === 'memory'}
          onPress={() => setSelectedTab('memory')}
        />
      </View>
      <ShareProfileModal isVisible={isShareModalVisible} setIsVisible={setIsShareModalVisible} />
      <DeleteModal
        isVisible={isInviteConfirmVisible}
        setIsVisible={setIsInviteConfirmVisible}
        title=""
        heading={t('ghostProfile.inviteConfirmTitle', { name: contactName })}
        description={
          invitedAt
            ? t('ghostProfile.inviteConfirmAgain', {
                name: contactName,
                date: dayjs(invitedAt).format('DD/MM/YYYY'),
                email: inviteEmail ?? '',
              })
            : t('ghostProfile.inviteConfirmBody', { email: inviteEmail ?? '' })
        }
        deleteLabel={t('ghostProfile.inviteSend')}
        onDelete={sendInviteToJoin}
      />
    </>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      paddingHorizontal: theme.spacing.xl,
      paddingVertical: theme.spacing.sm,
    },
    refreshContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      columnGap: 20,
      rowGap: theme.spacing.sm,
      paddingHorizontal: theme.spacing.lg,
    },
    refreshContainerEnd: {
      justifyContent: 'flex-end',
    },
    refreshContent: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      flexShrink: 1,
    },
    marginBottom: {
      marginBottom: theme.spacing.lg,
    },
    rightContainer: {
      paddingRight: theme.spacing.lg,
      marginBottom: theme.spacing.lg,
      // The rail (last-interaction + filter buttons) sits in the right column of
      // the desktop row, hugging the right edge. The bio now renders full-width
      // above this row, so it is never squeezed into a left column.
      alignSelf: 'flex-end',
      maxWidth: '100%',
    },
    refreshContentWeb: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingRight: theme.spacing.lg,
      marginBottom: theme.spacing.lg,
    },
    actionButtonsContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'stretch',
      paddingHorizontal: theme.spacing.sm,
      paddingBottom: theme.spacing.sm,
      marginBottom: theme.spacing.md,
      gap: theme.spacing.xs,
    },
  })
